import { useMemo } from 'react';
import {
  InputTable,
  type InputTableCellParams,
  type InputTableColumn,
  type InputTableRowParams,
  type RepeatRowsPayload
} from '@/components/custom/erp';
import type { PhiNganHangRow } from '../hooks/usePhiNganHangRows';
import { copyCellValue } from '@/lib/table-handlers';
import { SelectedCellInfo } from '../../../hooks';
import { getDataTableColumns } from './columns';
import { usePhiNganHangPaste } from '../hooks';
import { FormMode } from '@/types/form';
import ActionBar from './ActionBar';

interface PhiNganHangTabProps {
  formMode: FormMode;
  rows: PhiNganHangRow[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: (params: InputTableRowParams<PhiNganHangRow>) => void;
  onCellClick?: (params: InputTableCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: unknown) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<unknown[]>; resultField?: string }>
  ) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<PhiNganHangRow>) => void;
  isPasting?: boolean;
}

export const PhiNganHangTab: React.FC<PhiNganHangTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onCellClick,
  onRowClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  onRepeatForRows,
  isPasting
}) => {
  const columns: InputTableColumn[] = useMemo(
    () => getDataTableColumns(onCellValueChange, formMode),
    [onCellValueChange, formMode]
  );

  usePhiNganHangPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  return (
    <InputTable
      rows={rows}
      autoHeight
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={row => row?.uuid || ''}
      enableContextMenu
      onCopyData={({ columnValue, columnField, columnValues }) => {
        copyCellValue({
          columnField,
          columnValue,
          columnValues,
          fallback: text => console.log('Copy data per column:', text)
        });
      }}
      onRepeatForRows={onRepeatForRows}
      actionButtons={
        <ActionBar
          formMode={formMode}
          handleAddRow={onAddRow}
          handleDeleteRow={onDeleteRow}
          handleCopyRow={onCopyRow}
          handlePasteRow={onPasteRow}
          selectedCell={selectedCell}
          handleMoveRow={onMoveRow}
          handleExport={() => console.log('Export clicked')}
          handlePin={() => console.log('Pin clicked')}
          isPasting={isPasting}
        />
      }
    />
  );
};
